import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceAnchor,
  ExperienceAnchorType,
  ISupportingActor
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { Checkbox, Tooltip } from '@material-ui/core';
import { compact, keyBy } from 'lodash';
import { useMemo } from 'react';
import { ColoredChipDark } from '../../../../../components/ColoredChip';
import {
  ItemSorter,
  ItemSorters
} from '../../../../../components/GroupableList';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { Dictionary } from '../../../../../hooks/useDictionary';
import { useUnsafeTranslation } from '../../../../../i18next';
import { isTimestamp, Timestamp, toMoment } from '../../../../../services/time';
import { getUserValue, toFullName } from '../../../../../services/user';

type ColumnName =
  | 'name'
  | 'role'
  | 'location'
  | 'department'
  | 'team'
  | 'address'
  | 'hireDate'
  | string;

export type D = {
  participant: Doc<IUser>;
  supportingActors: Dictionary<{
    key: string;
    user: Doc<IUser>;
  }>;
  isSelectable: boolean;
};
export type OtherProps = {
  selected: Set<string>;
  templateAnchor: ExperienceAnchor;
  anchorLabel: string;
  n: Timestamp;
  selectAllCurrentlyVisible: () => void;
  deselectAllCurrentlyVisible: () => void;
  areCurrentAllSelected: boolean;
  dateFormat: string;
  tz: string;
};

export type ManualEnrollTableColumn = IColumn<D, ColumnName, OtherProps>;

const COLUMNS: ManualEnrollTableColumn[] = [
  {
    key: 'selector',
    head: (o) => {
      if (!o) {
        return;
      }
      return (
        <Checkbox
          checked={o.areCurrentAllSelected}
          onChange={(ev) => {
            const checked = ev.target.checked;
            if (checked) {
              o.selectAllCurrentlyVisible();
            } else {
              o.deselectAllCurrentlyVisible();
            }
          }}
        />
      );
    },
    cell: (d, o) => {
      if (!d.isSelectable) {
        return (
          <MissingAnchorTooltip title="To enroll this participant, add this data to your import source and run another import.">
            <Checkbox disabled={true} style={{ marginTop: 2 }} />
          </MissingAnchorTooltip>
        );
      }
      return (
        <Checkbox
          checked={o.selected.has(d.participant.id)}
          style={{ marginTop: 2 }}
        />
      );
    },
    align: 'left',
    sortable: false,
    width: 30,
    flexGrow: 0
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => {
      return toFullName(d.participant.data);
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'email',
    head: () => 'Work Email',
    cell: (d) => {
      return d.participant.data.email;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'personalEmail',
    head: () => 'Personal Email',
    cell: (d) => {
      return d.participant.data.personalEmail;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'phoneNumber',
    head: () => 'Phone Number',
    cell: (d) => {
      return d.participant.data.phoneNumber;
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => {
      return toFullName(d.participant.data);
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'role',
    head: () => 'Role',
    cell: (d) => d.participant.data.role,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'location',
    head: () => 'Location',
    cell: (d) => d.participant.data.location,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'department',
    head: () => 'Department',
    cell: (d) => d.participant.data.department,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'team',
    head: () => 'Team',
    cell: (d) => d.participant.data.team,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'hireDate',
    head: () => 'First day at work',
    cell: (d, o) => {
      return d.participant.data.hireDate
        ? toMoment(d.participant.data.hireDate).tz(o.tz).format(o.dateFormat)
        : '';
    },
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'anchorDate',
    head: (o) => {
      if (o?.templateAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
        return o.anchorLabel;
      }
    },
    cell: (d, o) => {
      const expAnchor = getUserAnchorFieldValue(d, o);
      return d.isSelectable && expAnchor ? (
        toMoment(expAnchor).tz(o.tz).format(o.dateFormat)
      ) : (
        <MissingAnchorTooltip title="To enroll this participant, add this data to your import source and run another import.">
          <MissingColoredChip />
        </MissingAnchorTooltip>
      );
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  }
];

export type ManualEnrollTableItemSorters = ItemSorters<D, OtherProps>;

export const SORTERS: ManualEnrollTableItemSorters = {
  name: {
    key: 'name',
    items: {
      sort: (d) => toFullName(d.participant.data),
      dir: 'asc'
    }
  },
  role: {
    key: 'role',
    items: { sort: (d) => d.participant.data.role, dir: 'asc' }
  },
  location: {
    key: 'location',
    items: { sort: (d) => d.participant.data.location, dir: 'asc' }
  },
  department: {
    key: 'department',
    items: { sort: (d) => d.participant.data.department, dir: 'asc' }
  },
  team: {
    key: 'team',
    items: { sort: (d) => d.participant.data.team, dir: 'asc' }
  },
  hireDate: {
    key: 'hireDate',
    items: {
      sort: (d) =>
        d.participant.data.hireDate
          ? d.participant.data.hireDate.toMillis()
          : Infinity,
      dir: 'asc'
    }
  },
  anchorDate: {
    key: 'anchorDate',
    items: {
      sort: (d, o) => {
        const expAnchor = getUserAnchorFieldValue(d, o);
        return expAnchor?.toMillis() || 0;
      },
      dir: 'asc'
    }
  }
};

export const getUserAnchorFieldValue = (d: D, o: OtherProps) => {
  const date =
    o.templateAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE
      ? getUserValue(d.participant.data, o.templateAnchor.field, o.n)
      : null;
  return isTimestamp(date) ? date : null;
};

const getCustomCell = (
  actor: ISupportingActor
): ((d: D, o: OtherProps) => React.ReactNode) => {
  return (d) =>
    d.supportingActors[actor.key]?.user
      ? toFullName(d.supportingActors[actor.key].user.data)
      : '';
};

const generateActorColumn = (
  actor: ISupportingActor
): ManualEnrollTableColumn => {
  return {
    key: actor.key,
    head: () => actor.name,
    cell: getCustomCell(actor),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  };
};

const generateSorter = (actor: ISupportingActor): ItemSorter<D, OtherProps> => {
  return {
    key: actor.key,
    items: {
      sort: (d) => {
        return d.supportingActors[actor.key]?.user
          ? toFullName(d.supportingActors[actor.key].user.data)
          : '';
      },
      dir: 'asc'
    }
  };
};

export const REQUIRED_MANUAL_ENROLL_COLUMNS = ['selector', 'name'];

export const CONTACT_MANUAL_ENROLL_COLUMNS = [
  'email',
  'personalEmail',
  'phoneNumber'
];

export const OTHER_MANUAL_ENROLL_COLUMNS = [
  'role',
  'team',
  'department',
  'location'
  // 'anchorDate'
];

export const useManualEnrollTableColumnsAndSorters = (
  actors: ISupportingActor[] = EMPTY_ARR,
  selectedColumnsInOrder: ColumnName[] = EMPTY_ARR
): {
  columns: ManualEnrollTableColumn[];
  sorters: ManualEnrollTableItemSorters;
} => {
  return useMemo(() => {
    const allColumns = [...COLUMNS, ...actors.map(generateActorColumn)];
    const sorters = { ...SORTERS };
    actors.forEach((actor) => (sorters[actor.key] = generateSorter(actor))); //TODO: figure out why sorters isn't working
    if (!selectedColumnsInOrder.length) {
      return { columns: allColumns, sorters };
    }
    const allColumnsByKey = keyBy(allColumns, (c) => c.key);
    console.log(
      'COLS',
      allColumns,
      selectedColumnsInOrder,
      compact(selectedColumnsInOrder.map((k) => allColumnsByKey[k]))
    );
    return {
      columns: compact(selectedColumnsInOrder.map((k) => allColumnsByKey[k])),
      sorters
    };
  }, [actors, selectedColumnsInOrder]);
};

const MissingAnchorTooltip: React.FC<{ title: string }> = ({
  title,
  children
}) => {
  const { t } = useUnsafeTranslation();
  const tooltipTitle = t(title);
  return (
    <Tooltip title={tooltipTitle}>
      <span>{children}</span>
    </Tooltip>
  );
};

const MissingColoredChip = () => {
  const { t } = useUnsafeTranslation();
  return (
    <ColoredChipDark color="red" size="small" variant="rounded">
      {t('Missing')}
    </ColoredChipDark>
  );
};
